import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import ArtifactItem from '../Artifacts/ArtifactItem';
import { FaCalendarAlt } from 'react-icons/fa';
import { calculateDurationMinutes } from "./time-utils";
import { useAuth } from "../../contexts/AuthContext";

const ArtifactList = ({ sessions, setActivePR, setActiveDoc, setActiveFigma, setActiveDiscord, setActiveFile, isAdminView = false, groupMembers, onSetDeadline, onViewResults, modificationCounts = {} }) => {
  const { courseInfo } = useAuth();
  const deadlineEnabled = courseInfo?.deadlineEnabled !== false;
  const [filterTag, setFilterTag] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [showTooltip, setShowTooltip] = useState(null);

  const getTime = (s) => new Date(`${s.date} ${s.startTime}`).getTime() || 0;
  const getDurationValue = (s) => calculateDurationMinutes(s.startTime, s.endTime, s.isCrossDate);
  const getDeadlineTime = (s) => {
    if (!s.reviewDeadline) return Infinity;
    return new Date(s.reviewDeadline).getTime();
  };

  const sessionRows = useMemo(() => {
    const rows = [...(sessions || [])];
    rows.sort((a, b) => {
      switch (sortBy) {
        case "durationDesc": {
          const da = getDurationValue(a);
          const db = getDurationValue(b);
          if (db !== da) return db - da;    // longest first
          return getTime(b) - getTime(a);   // tie-break: newer first
        }
        case "deadline": {
          const deadlineA = getDeadlineTime(a);
          const deadlineB = getDeadlineTime(b);
          if (deadlineA !== deadlineB) return deadlineA - deadlineB; // earliest deadline first
          return getTime(b) - getTime(a);   // tie-break: newer first
        }
        case "oldest":
          return getTime(a) - getTime(b);
        case "newest":
        default:
          return getTime(b) - getTime(a);
      }
    });
    return rows;
  }, [sessions, sortBy]);
  return (
    <StyledArtifactList>
      <FilterBar>
        <FilterFields>
          <Field>
            <Label htmlFor="filter">Filter by Tag</Label>
            <Select id="filter" value={filterTag} onChange={e => setFilterTag(e.target.value)}>
              <option value="all">All</option>
              <option value="pr">PR</option>
              <option value="file">File</option>
              <option value="googleDoc">Google Doc</option>
              <option value="figma">Figma</option>
              <option value="discord">Discord</option>
            </Select>
          </Field>
          <Field>
            <Label htmlFor="sort">Sort by</Label>
            <Select id="sort" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="newest">Newest date (Default)</option>
              <option value="oldest">Oldest date</option>
              <option value="durationDesc">Longest duration</option>
              <option value="deadline">Deadline (earliest first)</option>
            </Select>
          </Field>
        </FilterFields>

      </FilterBar>
      <Container>
        <ListHeader $deadlineEnabled={deadlineEnabled}>
          <span className="name-column">Name</span>
          <span className="tag-column">
            Tag
            <InfoIconContainer
              onMouseEnter={() => setShowTooltip('tag')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <InfoIcon>ⓘ</InfoIcon>
              {showTooltip === 'tag' && (
                <Tooltip>
                  <strong>TAG MEANINGS:</strong>
                  <br />PR = GitHub Pull Request
                  <br />GDoc = Google Document
                  <br />File = Uploaded Document
                  <br />Figma = Figma File
                  <br />Discord = Discord chat
                </Tooltip>
              )}
            </InfoIconContainer>
          </span>
          <span className="participants-column">Participants</span>
          <span className="activity-column">Activity</span>
          <span className="date-column">Date</span>
          <span className="start-column">Start Time</span>
          <span className="end-column">End Time</span>
          <span className="duration-column">Duration</span>
          {deadlineEnabled && (
            <span className="review-progress-column">
              Review
              <InfoIconContainer
                onMouseEnter={() => setShowTooltip('review')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <InfoIcon>ⓘ</InfoIcon>
                {showTooltip === 'review' && (
                  <Tooltip>
                    <strong>REVIEW STATUS:</strong>
                    <br />X/Y = X members completed out of Y total
                  </Tooltip>
                )}
              </InfoIconContainer>
            </span>
          )}
          {deadlineEnabled && (
            <span className="actions-column">
              Actions
              <InfoIconContainer
                onMouseEnter={() => setShowTooltip('actions')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <InfoIcon>ⓘ</InfoIcon>
                {showTooltip === 'actions' && (
                  isAdminView ? (
                    <AdminActionsTooltip>
                      <strong>ADMIN ACTIONS:</strong>
                      <br /><ActionName>View Results</ActionName> - View all ratings and compiled feedback for this artifact
                    </AdminActionsTooltip>
                  ) : (
                    <ActionsTooltip>
                      <strong>ACTIONS MEANINGS:</strong>
                      <br /><ActionName>Submit Rating</ActionName> - Submit your rating for this artifact
                      <br /><ActionName>Edit My Rating</ActionName> - Modify your previously submitted rating
                      <br /><ActionName>Re-Rate</ActionName> - The artifact was updated after you submitted your rating. Please re-rate with the latest version
                      <br /><ActionName>View My Rating</ActionName> - The deadline has passed. You can view your submitted rating but can no longer edit it
                      <br /><ActionName>Rating Closed</ActionName> - The 7-day rating window has closed and no submission was made
                    </ActionsTooltip>
                  )
                )}
              </InfoIconContainer>
            </span>
          )}
        </ListHeader>
        {sessionRows.map((session, i) => (
          <ArtifactItem
            key={session?.id || i}
            session={session}
            setActivePR={setActivePR}
            filterTag={filterTag}
            setActiveDoc={setActiveDoc}
            setActiveFigma={setActiveFigma}
            setActiveDiscord={setActiveDiscord}
            setActiveFile={setActiveFile}
            isAdminView={isAdminView}
            groupMembers={groupMembers}
            onSetDeadline={onSetDeadline}
            onViewResults={onViewResults}
            modificationCount={modificationCounts[session?.eventId] || 0}
          />
        ))}
      </Container>
    </StyledArtifactList>
  );
};

const StyledArtifactList = styled.section`
    margin-top: 20px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
`;

// Container for the whole artifact list
const Container = styled.div`
  background-color: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  min-width: 1380px;
`;

const ListHeader = styled.div`
  display: grid;
  grid-template-columns: ${({ $deadlineEnabled }) =>
    $deadlineEnabled
      ? 'minmax(0,2.8fr) minmax(45px,0.75fr) minmax(135px,1fr) minmax(180px,3fr) minmax(130px,1.2fr) minmax(130px,1.2fr) minmax(130px,1.2fr) minmax(0,0.85fr) minmax(100px,1.2fr) minmax(150px,1.8fr)'
      : 'minmax(0,2.8fr) minmax(45px,0.75fr) minmax(135px,1fr) minmax(180px,3fr) minmax(130px,1.2fr) minmax(130px,1.2fr) minmax(130px,1.2fr) minmax(0,0.85fr)'};
  border-bottom: 1px solid #E5E7EB;
  padding: 1rem 1.5rem;
  background-color: #F9FAFB;

  > span {
    padding: 0.75rem 8px;         /* px-6 py-3 */
    text-align: left;               /* text-left */
    font-size: 0.85rem;             /* text-xs */
    font-weight: 500;               /* font-medium */
    color: #6B7280;                 /* text-gray-500 */
    text-transform: uppercase;      /* uppercase */
    letter-spacing: 0.05em;         /* tracking-wider */
    min-width: 0;
    word-break: break-word;
  }
`;

const FilterBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    font-size: 14px;
    color: #374151;
    padding: 0 1rem;
`;

const FilterFields = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;


const Select = styled.select`
  margin-top: 0.25rem;       /* mt-1 */
  display: block;
  width: 100%;               /* w-full */
  padding: 0.5rem 2.5rem 0.5rem 0.75rem; /* py-2 pr-10 pl-3 */
  font-size: 1rem;           /* text-base */
  color: #374151;  
  background-color: #f3f4f6;
  border: none; /* border-gray-300 */
  border-radius: 0.375rem;   /* rounded-md */
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;

  /* remove native arrow and add our own */
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 8l4 4 4-4' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center; /* pr-10 space accounted for */
  background-size: 1rem;

  &:focus {
    border: 1px solid #9333ea;
    box-shadow: 0 0 0 3px rgba(99,102,241,.35); /* focus ring */
  }
`;

const Field = styled.div`
  margin-bottom: 1rem;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;       /* text-sm */
  font-weight: 500;          /* font-medium */
  color: #374151;            /* text-gray-700 */
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;


const InfoIconContainer = styled.span`
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 5px;
`;

const InfoIcon = styled.span`
  font-size: 14px;
  color: #666;
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e0e0e0;
    border-color: #999;
    color: #333;
  }
`;

const Tooltip = styled.div`
  text-transform: none;
  white-space: normal;
  position: absolute;
  top: 25px;
  right: 0;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 12px;
  width: 240px;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  line-height: 1.5;
  color: #333;
  text-align: left;
`;

const ActionsTooltip = styled.div`
  text-transform: none;
  white-space: normal;
  position: absolute;
  top: 25px;
  right: 0;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 12px 16px;
  width: 420px;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  line-height: 1.6;
  color: #333;
  text-align: left;
`;

const AdminActionsTooltip = styled.div`
  text-transform: none;
  white-space: normal;
  position: absolute;
  top: 25px;
  right: 0;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 12px 14px;
  width: 320px;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  line-height: 1.5;
  color: #333;
  text-align: left;
`;

const ActionName = styled.span`
  font-weight: 600;
  color: #93c5fd;
`;


export default ArtifactList;